import type { Header, Table } from '@tanstack/react-table';

import { fontStyles } from '@/styles/typography';

import { ChevronDownSmallFilledIcon } from '@/ui/Icons/ChevronDownSmallFilledIcon';

import { classNames } from '@/utils/classnames';
import type { SortColumn, SortDirection } from '@/utils/zerion/types';

import type { PortfolioRow } from '../types';

type Props = {
  title: string;
  property: SortColumn;
  sortable?: boolean;
  table: Table<PortfolioRow>;
  header: Header<PortfolioRow, unknown>;
};

const getSortDirection = (
  table: Table<PortfolioRow>,
  header: Header<PortfolioRow, unknown>,
): SortDirection | undefined => {
  const { sorting } = table.getState();
  const sortEntry = sorting.find(({ id }) => id === header.id);
  if (!sortEntry) return undefined;
  return sortEntry.desc ? 'desc' : 'asc';
};

/** Column label with the sort chevron; `sortDirection` set means this column is the active sort. */
export function SortableHeader({
  title,
  sortable,
  sortDirection,
  onSort,
}: {
  title: string;
  sortable?: boolean;
  sortDirection?: SortDirection;
  onSort?: () => void;
}) {
  const isActive = sortDirection !== undefined;

  return (
    <button
      className={classNames(
        'flex items-center gap-0.5 transition-colors',
        sortable ? 'hover:text-token-gray-1600 cursor-pointer' : '',
      )}
      onClick={sortable ? onSort : undefined}
    >
      <span
        className={fontStyles({
          variant: 'LABEL_SM',
          class: isActive ? 'text-token-gray-1600 font-medium' : 'text-token-gray-700',
        })}
      >
        {title}
      </span>
      {sortable && (
        <ChevronDownSmallFilledIcon
          className={classNames(
            'size-3',
            sortDirection === 'asc' ? 'rotate-180' : '',
            isActive ? 'text-token-gray-1600' : 'text-transparent',
          )}
        />
      )}
    </button>
  );
}

export function PortfolioHeaderCell({ title, property, sortable, table, header }: Props) {
  const sortDirection = getSortDirection(table, header);

  return (
    <SortableHeader
      title={title}
      sortable={sortable}
      sortDirection={sortDirection}
      onSort={() => table.setSorting([{ id: property, desc: sortDirection !== 'desc' }])}
    />
  );
}
